import { useState } from 'react';
import type { InsightCitation } from '../lib/api';

// Human-readable labels for federated citation sources
const SOURCE_TYPE_LABELS: Record<string, string> = {
  insight: 'R&D insight',
  strategy_demand: 'Strategy demand',
  commitment: 'Commitment',
  sentiment: 'Sentiment',
  article: 'Market article',
  transcript: 'Transcript',
};

interface CitationChipProps {
  marker: number;
  citation?: InsightCitation;
}

export function CitationChip({ marker, citation }: CitationChipProps) {
  const [expanded, setExpanded] = useState(false);

  if (!citation) {
    // Fallback if citation data not found - just show the marker
    return <span className="citation-chip citation-chip-unknown">[{marker}]</span>;
  }

  return (
    <span className="citation-chip-wrapper">
      <button
        className={`citation-chip ${expanded ? 'citation-chip-expanded' : ''}`}
        onClick={() => setExpanded(!expanded)}
        title={`Click to ${expanded ? 'collapse' : 'expand'} citation`}
      >
        [{marker}]
      </button>
      {expanded && (
        <span className="citation-detail">
          <span className="citation-quote">"{citation.quote}"</span>
          <span className="citation-source">
            <span className="citation-client">{citation.client_name}</span>
            <span className="citation-date">{citation.meeting_date}</span>
            {citation.source_type && citation.source_type !== 'insight' && (
              <span className="citation-source-type">
                {SOURCE_TYPE_LABELS[citation.source_type] || citation.source_type}
              </span>
            )}
            {citation.urgency && (
              <span className={`citation-urgency urgency-${citation.urgency.toLowerCase().replace(' ', '-')}`}>
                {citation.urgency}
              </span>
            )}
          </span>
        </span>
      )}
    </span>
  );
}

interface RenderWithCitationsProps {
  content: string;
  citations?: InsightCitation[];
}

/**
 * Renders answer content with interactive citation chips.
 * Parses [1], [2], [3] markers and replaces them with CitationChip components.
 */
export function RenderWithCitations({ content, citations }: RenderWithCitationsProps) {
  // Build citation lookup map
  const citationMap = new Map<number, InsightCitation>();
  if (citations && citations.length > 0) {
    citations.forEach(c => citationMap.set(c.marker, c));
  }

  // Split content by citation markers [1], [2], [3], etc.
  const parts = content.split(/(\[\d+\])/g);

  return (
    <>
      {parts.map((part, i) => {
        // Check if this part is a citation marker
        const markerMatch = part.match(/^\[(\d+)\]$/);
        if (markerMatch) {
          const marker = parseInt(markerMatch[1], 10);
          const citation = citationMap.get(marker);
          return <CitationChip key={i} marker={marker} citation={citation} />;
        }

        // Regular text - apply markdown formatting
        if (part.trim()) {
          const html = part
            .replace(/^### (.+)$/gm, '<h3>$1</h3>')
            .replace(/^## (.+)$/gm, '<h2>$1</h2>')
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.+?)\*/g, '<em>$1</em>')
            .replace(/^- (.+)$/gm, '<li>$1</li>')
            .replace(/\n\n/g, '</p><p>')
            .replace(/\n/g, '<br/>');

          return <span key={i} dangerouslySetInnerHTML={{ __html: html }} />;
        }

        return null;
      })}
    </>
  );
}
